<template>
  <nav class="flex-1 px-2 pb-4 space-y-8 mt-4">
    <!-- Top-level items (no group header) -->
    <div class="space-y-0.5">
      <template v-for="item in topLevelItems" :key="item.name">
        <router-link
          :to="item.to"
          class="flex items-center px-2 py-1.5 rounded-md group"
          :class="[
            isActiveRoute(item) ? 'text-primary-dark font-medium' : '',
            'group flex items-center px-2 py-1.5 text-sm hover:bg-gray-200 rounded-md text-gray-500',
          ]"
          @click="$emit('close')"
        >
          <component
            :is="item.icon"
            class="h-4 w-4 mr-2"
            :class="isActiveRoute(item) ? 'text-primary-dark' : 'text-gray-400'"
            aria-hidden="true"
          />
          <span>{{ $t("Navigation." + item.name) }}</span>
        </router-link>
      </template>
    </div>

    <!-- Grouped sections -->
    <template v-for="group in navigationGroups" :key="group.key">
      <div v-if="group.items.length">
        <p class="px-2 mb-1 text-xs font-extralight text-gray-800 uppercase tracking-wider">
          {{ $t("Navigation." + group.key) }}
        </p>
        <div class="space-y-0.5">
          <template v-for="item in group.items" :key="item.name">
            <div>
              <!-- Parent navigation item with children -->
              <div
                v-if="visibleChildren(item).length"
                @click="toggleItem(item.name)"
                class="flex items-center justify-between cursor-pointer px-2 py-1.5 rounded-md group"
                :class="[
                  isActiveRoute(item) ? 'text-primary-dark font-medium' : '',
                  'group flex items-center px-2 py-1.5 text-sm hover:bg-gray-200 rounded-md text-gray-500',
                ]"
              >
                <div class="flex items-center space-x-2">
                  <component
                    :is="item.icon"
                    class="h-4 w-4"
                    :class="isActiveRoute(item) ? 'text-primary-dark' : 'text-gray-400'"
                    aria-hidden="true"
                  />
                  <span>{{ $t("Navigation." + item.name) }}</span>
                </div>
                <component
                  :is="isExpanded(item.name) ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                  class="h-4 w-4 text-gray-400 transition-transform duration-200"
                />
              </div>

              <!-- Simple navigation item -->
              <router-link
                v-else
                :to="item.to"
                class="flex items-center px-2 py-1.5 rounded-md group"
                :class="[
                  isActiveRoute(item) ? 'text-primary-dark font-medium' : '',
                  'group flex items-center px-2 py-1.5 text-sm hover:bg-gray-200 rounded-md text-gray-500',
                ]"
                @click="$emit('close')"
              >
                <component
                  :is="item.icon"
                  class="h-4 w-4 mr-2"
                  :class="isActiveRoute(item) ? 'text-primary-dark' : 'text-gray-400'"
                  aria-hidden="true"
                />
                <span class="flex-1">{{ $t("Navigation." + item.name) }}</span>
                <span v-if="item.pulse" class="relative flex h-2 w-2">
                  <span
                    class="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"
                  ></span>
                  <span class="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                </span>
              </router-link>

              <!-- Children navigation items -->
              <transition
                @before-enter="beforeEnter"
                @enter="enter"
                @after-enter="afterEnter"
                @before-leave="beforeLeave"
                @leave="leave"
                @after-leave="afterLeave"
              >
                <div
                  v-if="visibleChildren(item).length && isExpanded(item.name)"
                  class="ml-5 border-l border-gray-200 pl-2 overflow-hidden"
                >
                  <router-link
                    v-for="child in visibleChildren(item)"
                    :key="child.name"
                    :to="child.to"
                    class="flex items-center px-3 py-1.5 text-sm rounded-md"
                    :class="[
                      isActiveRoute(child)
                        ? 'bg-primary-50 text-primary-dark font-medium'
                        : 'text-gray-500 hover:bg-gray-100',
                    ]"
                    @click="$emit('close')"
                  >
                    {{ $t("Navigation." + child.name) }}
                  </router-link>
                </div>
              </transition>
            </div>
          </template>
        </div>
      </div>
    </template>
  </nav>
</template>

<script>
import {
  CurrencyEuroIcon,
  HomeIcon,
  ArchiveIcon,
  SparklesIcon,
  FireIcon,
  ViewListIcon,
  UserCircleIcon,
  PresentationChartLineIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  BookOpenIcon,
  CogIcon,
  GlobeIcon,
} from "@heroicons/vue/outline";
import { mapGetters, mapState } from "vuex";
import { ClipboardListIcon } from "@heroicons/vue/solid";

export default {
  components: {
    CurrencyEuroIcon,
    HomeIcon,
    ArchiveIcon,
    SparklesIcon,
    FireIcon,
    ViewListIcon,
    UserCircleIcon,
    PresentationChartLineIcon,
    ChevronUpIcon,
    ChevronDownIcon,
    BookOpenIcon,
    CogIcon,
    GlobeIcon,
  },

  data() {
    return {
      expandedItems: new Set(),
    };
  },

  mounted() {
    this.autoExpandActiveItem();
  },

  watch: {
    $route() {
      this.autoExpandActiveItem();
    },
  },

  computed: {
    ...mapState("auth", ["currentUser"]),
    ...mapGetters("auth", ["currentUserHasPermission", "hasRetailAnalytics", "isWebOrganization", "isRegionalManager"]),
    ...mapGetters("businessVerticals", ["hasSeatsOrFurniture"]),
    ...mapGetters("challenge", ["hasActiveChallenge"]),

    topLevelItems() {
      return [
        {
          name: "Home",
          to: "/admin/home",
          icon: HomeIcon,
          permission: "admin",
          navigation: "Home",
        },
      ].filter((item) => this.currentUserHasPermission(item.permission));
    },

    navigationGroups() {
      return [
        {
          key: "group_performance",
          items: [
            this.hasRetailAnalytics
              ? {
                  name: "Retail analytics",
                  to: "/admin/analytics/retail",
                  icon: PresentationChartLineIcon,
                  permission: "admin",
                  navigation: "Retail analytics",
                }
              : null,
            this.hasRetailAnalytics && (this.hasSeatsOrFurniture || this.isRegionalManager)
              ? {
                  name: "Region analytics",
                  to: "/admin/analytics/retail/regions",
                  icon: GlobeIcon,
                  permission: "admin",
                  navigation: "Region analytics",
                }
              : null,
            this.hasRetailAnalytics && !this.isRegionalManager
              ? {
                  name: "Roadmap",
                  to: "/admin/roadmap",
                  icon: ClipboardListIcon,
                  permission: "admin",
                }
              : null,
            {
              name: "Remuneration",
              to: "/admin/billing/invoices",
              icon: CurrencyEuroIcon,
              permission: "invoices",
            },
            !this.isWebOrganization
              ? {
                  name: "Retributions",
                  to: "/admin/retributions",
                  icon: SparklesIcon,
                  permission: "retributions",
                }
              : null,
            this.hasActiveChallenge
              ? {
                  name: "Challenge",
                  to: "/admin/challenge/current-challenge",
                  icon: FireIcon,
                  permission: "admin",
                  pulse: true,
                }
              : null,
          ].filter((item) => item && this.currentUserHasPermission(item.permission)),
        },
        {
          key: "group_management",
          items: [
            {
              name: "Plan sales",
              to: "/admin/plan-sales",
              icon: ArchiveIcon,
              permission: "plan_sales",
            },
            {
              name: "Claim List",
              to: "/admin/claims",
              icon: ViewListIcon,
              permission: "claims",
              children: [
                {
                  name: "Claim management",
                  to: "/admin/claims",
                  permission: "invoices",
                },
                {
                  name: "Claim invoices",
                  to: "/admin/claim_invoices",
                  permission: "invoices",
                },
              ],
            },
          ].filter((item) => item && this.currentUserHasPermission(item.permission)),
        },
        {
          key: "group_settings",
          items: [
            {
              name: "My organization",
              to: "/admin/organization/details",
              icon: HomeIcon,
              permission: "stores",
              children: [
                {
                  name: "General informations",
                  to: "/admin/organization/details",
                  permission: "stores",
                },
                {
                  name: "Stores",
                  to: "/admin/organization/stores",
                  permission: "stores",
                },
              ],
            },
            {
              name: "Users",
              to: "/admin/users",
              icon: UserCircleIcon,
              permission: "users",
            },
            !this.isWebOrganization
              ? {
                  name: "Retributions configuration",
                  to: "/admin/retribution-configurations",
                  icon: CogIcon,
                  permission: "invoices",
                }
              : null,
            !this.isWebOrganization
              ? {
                  name: "Training",
                  to: "/admin/training",
                  icon: BookOpenIcon,
                  permission: "admin",
                }
              : null,
          ].filter((item) => item && this.currentUserHasPermission(item.permission)),
        },
      ];
    },
  },

  methods: {
    visibleChildren(item) {
      if (!item.children) return [];
      return item.children.filter((c) => !c.permission || this.currentUserHasPermission(c.permission));
    },
    autoExpandActiveItem() {
      this.expandedItems.clear();
      for (const group of this.navigationGroups) {
        for (const item of group.items) {
          if (this.visibleChildren(item).length && this.isActiveRoute(item)) {
            this.expandedItems.add(item.name);
            return;
          }
        }
      }
    },
    isActiveRoute(item) {
      if (item.navigation) return item.navigation === this.$route.meta?.navigation;
      if (item.children) return item.children.some((child) => this.isActiveRoute(child));
      return this.$route.path.startsWith(item.to);
    },
    toggleItem(itemName) {
      if (this.expandedItems.has(itemName)) {
        this.expandedItems.delete(itemName);
      } else {
        this.expandedItems.clear();
        this.expandedItems.add(itemName);
      }
    },
    isExpanded(itemName) {
      return this.expandedItems.has(itemName);
    },
    // Transition hooks for smooth slide
    beforeEnter(el) {
      el.style.height = "0";
      el.style.opacity = "0";
    },
    enter(el) {
      el.style.transition = "height 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.2s";
      el.style.height = el.scrollHeight + "px";
      el.style.opacity = "1";
    },
    afterEnter(el) {
      el.style.height = "auto";
      el.style.transition = "";
    },
    beforeLeave(el) {
      el.style.height = el.scrollHeight + "px";
      el.style.opacity = "1";
    },
    leave(el) {
      el.style.transition = "height 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.2s";
      el.style.height = "0";
      el.style.opacity = "0";
    },
    afterLeave(el) {
      el.style.transition = "";
      el.style.height = "auto";
    },
  },
};
</script>
